/**
 * CoverageCard - Display card for Finch Coverage entities
 *
 * Styled to match MedicalProviderCard/FilevineEntityCard with a preview
 * and Details link that navigates to the coverage detail page.
 */

import { ExternalLink, Shield } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CoverageEntitySchema } from '@/services/api/generated/entitySyncService';

import { formatEnumValue } from '../../shared/EntityFieldComponents';

interface CoverageCardProps {
  coverage: CoverageEntitySchema;
  matterId?: string;
  className?: string;
  compact?: boolean;
}

/** Get status badge color */
function getStatusColor(status: string): string {
  const statusLower = status.toLowerCase();
  if (statusLower === 'approved') return 'bg-emerald-100 text-emerald-700';
  if (statusLower === 'rejected' || statusLower === 'not_applicable')
    return 'bg-red-100 text-red-700';
  if (statusLower === 'needs_approval') return 'bg-amber-100 text-amber-700';
  return 'bg-zinc-100 text-zinc-700';
}

/** Get category badge color */
function getCategoryColor(category: string): string {
  const catLower = category.toLowerCase();
  if (catLower.includes('first_party')) return 'bg-blue-100 text-blue-700';
  if (catLower.includes('third_party')) return 'bg-violet-100 text-violet-700';
  return 'bg-zinc-100 text-zinc-700';
}

export const CoverageCard: React.FC<CoverageCardProps> = ({
  coverage,
  matterId,
  className,
  compact = false,
}) => {
  const detailUrl = matterId
    ? `/matters/${matterId}/coverage/${coverage.id}`
    : null;

  const displayName =
    coverage.carrier_name || coverage.policy_type
      ? [coverage.carrier_name, coverage.policy_type && formatEnumValue(coverage.policy_type)]
          .filter(Boolean)
          .join(' - ')
      : `Coverage ${coverage.id.slice(0, 8)}`;

  return (
    <div
      className={cn(
        'flex rounded-lg border border-zinc-200 bg-white',
        compact ? 'p-2' : 'p-3',
        className,
      )}
    >
      {/* Left: Preview content */}
      <div className="flex-1 space-y-1 text-sm">
        {/* Name with icon */}
        <div className="flex items-center gap-1.5 text-zinc-900 font-medium">
          <Shield className="h-3.5 w-3.5 text-zinc-500" />
          {displayName}
        </div>

        {/* Policy identifiers */}
        {coverage.policy_identifiers && coverage.policy_identifiers.length > 0 && (
          <div className="text-xs text-zinc-500 pl-5">
            {coverage.policy_identifiers
              .map((p) => `${formatEnumValue(p.identifier_type)}: ${p.identifier_value}`)
              .join(', ')}
          </div>
        )}

        {/* Badges row */}
        <div className="flex flex-wrap gap-1 pl-5">
          {coverage.category && (
            <Badge
              variant="secondary"
              className={cn('text-xs', getCategoryColor(coverage.category))}
            >
              {formatEnumValue(coverage.category)}
            </Badge>
          )}
          {coverage.status && (
            <Badge
              variant="secondary"
              className={cn('text-xs', getStatusColor(coverage.status))}
            >
              {formatEnumValue(coverage.status)}
            </Badge>
          )}
        </div>
      </div>

      {/* Right: Details link */}
      {detailUrl && (
        <div className="flex shrink-0 flex-col justify-end">
          <Link
            to={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-6 items-center gap-1 px-2 text-xs text-zinc-500 hover:text-zinc-700"
          >
            <ExternalLink className="h-3 w-3" />
            Details
          </Link>
        </div>
      )}
    </div>
  );
};

export default CoverageCard;
